import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, ArrowDown, ArrowUp, CheckCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modal shown to a client via the chat three-dot menu.
 * kind = 'withdraw' → last 5 paid withdraw entries.
 * kind = 'deposit'  → last 5 deposit_done sticker entries.
 */
const ClientHistoryModal = ({ open, kind, customerId, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    const url = kind === 'deposit'
      ? `${API}/client/${customerId}/deposit-history`
      : `${API}/client/${customerId}/withdraw-history`;
    setLoading(true);
    axios.get(url)
      .then(res => setEntries(res.data?.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [open, kind, customerId]);

  if (!open) return null;

  const isDeposit = kind === 'deposit';
  const accent = isDeposit ? '#3b82f6' : '#10b981';
  const title = isDeposit ? 'Deposit History' : 'Withdraw History';
  const Icon = isDeposit ? ArrowDown : ArrowUp;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl w-full max-w-sm max-h-[80vh] flex flex-col overflow-hidden shadow-2xl" data-testid={`client-${kind}-history-modal`}>
        <div className="p-4 flex items-center justify-between text-white" style={{ background: accent }}>
          <h3 className="text-base font-bold flex items-center gap-2"><Icon size={18} weight="bold" /> {title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">
              No {isDeposit ? 'deposit' : 'paid withdraw'} entries yet
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map(e => (
                <div key={e.id} className="rounded-lg border border-gray-200 p-3 bg-gray-50" data-testid={`client-history-entry-${e.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-lg font-bold" style={{ color: accent }}>₹ {e.amount}</div>
                      {(e.bank_snapshot?.bank_account_number || e.bank_snapshot?.upi_id) && (
                        <div className="text-[11px] text-gray-600 mt-0.5 leading-tight">
                          {e.bank_snapshot?.bank_account_number && (
                            <div className="font-mono">A/c: •••• {String(e.bank_snapshot.bank_account_number).slice(-4)}</div>
                          )}
                          {e.bank_snapshot?.upi_id && <div>UPI: {e.bank_snapshot.upi_id}</div>}
                          {e.bank_snapshot?.bank_name && <div>{e.bank_snapshot.bank_name}</div>}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {!isDeposit && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-green-600 text-white px-2 py-0.5 rounded">
                          <CheckCircle size={10} weight="fill" /> Paid
                        </span>
                      )}
                      <div className="text-[10px] text-gray-500 mt-1">
                        {new Date(isDeposit ? e.created_at : (e.paid_at || e.created_at)).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className="text-[10px] text-gray-400 text-center py-2">Showing last {entries.length} entries</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientHistoryModal;
